import {
    Component,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import {
    ORGANIZATION_CONTEXT_SUBSCRIPTION_PLANS,
    PRIVATE_CONTEXT_SUBSCRIPTION_PLANS,
    SEATS,
    SUBSCRIPTION_META,
    SubscriptionPlan,
} from '../../../../models/subscription';
import {
    SessionUrlDto,
    SubscriptionDto,
    SubscriptionType,
} from '../../../../models/credit-service-dtos';
import { CreditsService } from '../../../../services/credits.service';
import { TranslateService } from '@ngx-translate/core';
import { nonNil } from '../../../../utility';
import { ToastMessageService } from 'src/app/shared/services/toast-message.service';
import { SubscriptionService } from 'src/app/shared/services/subscription.service';
import { delay, Observable } from 'rxjs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ChecksModalComponent } from 'src/app/shared/components/checks-modal/checks-modal.component';
import { Check } from 'src/app/split-screen/models/training-prestart-check';

@Component({
    selector: 'app-subscriptions',
    templateUrl: './subscriptions.component.html',
    styleUrl: './subscriptions.component.scss',
    standalone: false,
})
export class SubscriptionsComponent implements OnInit, OnChanges {
    protected readonly SUBSCRIPTION_META = SUBSCRIPTION_META;
    protected readonly SEATS = SEATS;
    protected readonly SUBSCRIPTION_TYPE = SubscriptionType;

    @Input() userId: string;
    @Input() activeOrganization: string;

    subscriptionPlans: SubscriptionPlan[];
    currentSubscriptionType: SubscriptionType;
    nextSubscriptionType: SubscriptionType | undefined;
    currentSubscriptionEndDate: Date;
    isLoading: boolean = true;
    isUpdatingSubscription: boolean = false;
    isCreatingCustomerPortalSession: boolean = false;
    showTimeLimitedSubscriptionDialog: boolean = false;

    SUBSCRIPTION_UPDATE_WAITING_DURATION = 3000;

    constructor(
        private creditsService: CreditsService,
        private translateService: TranslateService,
        private toastService: ToastMessageService,
        private subscriptionService: SubscriptionService,
        private dialog: MatDialog
    ) {}

    ngOnInit(): void {
        this.fetchSubscription();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes.activeOrganization &&
            !changes.activeOrganization.isFirstChange()
        ) {
            this.fetchSubscription();
        }
    }

    private fetchSubscription() {
        this.isLoading = true;

        this.setSubscriptionTypesBasedOnContext();
        const currentLang = this.translateService.getCurrentLang();

        if (currentLang === 'en') {
            this.subscriptionPlans.map((plan) => {
                plan.price = plan.price.replace(',', '.');
                return plan;
            });
        }

        this.creditsService.getCurrentUserSubscription(this.userId).subscribe({
            next: (subscriptionDto: SubscriptionDto) => {
                this.currentSubscriptionType =
                    subscriptionDto?.subscriptionType;
                this.nextSubscriptionType =
                    subscriptionDto?.nextSubscriptionType;
                this.currentSubscriptionEndDate = subscriptionDto?.endDate;
                this.isLoading = false;
                if (subscriptionDto && !subscriptionDto.isAutoRenew) {
                    this.openTimeLimitedSubscriptionDialog();
                }
            },
            error: () => {
                this.isLoading = false;
            },
        });
    }

    private setSubscriptionTypesBasedOnContext() {
        this.subscriptionPlans = JSON.parse(
            JSON.stringify(
                !!this.activeOrganization
                    ? ORGANIZATION_CONTEXT_SUBSCRIPTION_PLANS
                    : PRIVATE_CONTEXT_SUBSCRIPTION_PLANS
            )
        );
    }

    onChooseSubscription(subscriptionType: SubscriptionType) {
        this.isLoading = true;
        if (this.hasPaidPlan()) {
            this.isUpdatingSubscription = true;
            let update: Observable<void>;
            if (subscriptionType === SubscriptionType.FREE) {
                update = this.subscriptionService.cancelSubscription(true);
            } else {
                update =
                    this.subscriptionService.changeSubscription(
                        subscriptionType
                    );
            }
            update
                .pipe(delay(this.SUBSCRIPTION_UPDATE_WAITING_DURATION))
                .subscribe({
                    next: () => {
                        this.fetchSubscription();
                        this.isUpdatingSubscription = false;
                    },
                    error: (error) => {
                        this.isLoading = false;
                        this.isUpdatingSubscription = false;
                        if (error?.error?.checks) {
                            this.openChecksModal(error.error.checks);
                        } else {
                            this.toastService.showTranslatedErrorMsg(
                                'shared.components.subscriptionsPage.components.subscriptions.errors.subscriptionChange',
                                {}
                            );
                        }
                    },
                });
        } else {
            this.creditsService
                .createCheckoutSession({
                    priceKey: subscriptionType,
                })
                .subscribe({
                    next: (sessionUrlDto: SessionUrlDto) =>
                        this.navigateTo(sessionUrlDto.url),
                    error: () => {
                        this.isLoading = false;
                        this.toastService.showTranslatedErrorMsg(
                            'shared.components.subscriptionsPage.components.subscriptions.errors.createCheckoutSession',
                            {}
                        );
                    },
                });
        }
    }

    onCancelSubscription(isCancel: boolean) {
        this.isUpdatingSubscription = true;
        this.isLoading = true;
        this.subscriptionService
            .cancelSubscription(isCancel)
            .pipe(delay(this.SUBSCRIPTION_UPDATE_WAITING_DURATION))
            .subscribe({
                next: () => {
                    this.fetchSubscription();
                    this.isUpdatingSubscription = false;
                },
                error: (error) => {
                    this.isLoading = false;
                    this.isUpdatingSubscription = false;
                    if (error?.error?.checks) {
                        this.openChecksModal(error.error.checks);
                    } else {
                        this.toastService.showTranslatedErrorMsg(
                            'shared.components.subscriptionsPage.components.subscriptions.errors.subscriptionChange',
                            {}
                        );
                    }
                },
            });
    }

    openChecksModal(checks: Check[]) {
        const config = new MatDialogConfig();
        config.data = {
            checks,
            internationalizationPrefix:
                'shared.components.subscriptionsPage.components.subscriptions.subscriptionChangeCheckModal',
        };
        config.autoFocus = false;

        this.dialog
            .open(ChecksModalComponent, config)
            .afterClosed()
            .subscribe();
    }

    onNavigateToCustomerPortal() {
        if (this.isCreatingCustomerPortalSession) {
            return;
        }
        this.isCreatingCustomerPortalSession = true;
        this.creditsService.createCustomerPortalSession().subscribe({
            next: (sessionUrlDto: SessionUrlDto) => {
                this.isCreatingCustomerPortalSession = false;
                this.navigateTo(sessionUrlDto.url);
            },
            error: () => {
                this.isCreatingCustomerPortalSession = false;
                this.toastService.showTranslatedErrorMsg(
                    'shared.components.subscriptionsPage.components.subscriptions.errors.createCustomerPortalSession',
                    {}
                );
            },
        });
    }

    hasPaidPlan(): boolean {
        return (
            nonNil(this.currentSubscriptionType) &&
            ![SubscriptionType.FREE, SubscriptionType.UNLIMITED].includes(
                this.currentSubscriptionType
            )
        );
    }

    openTimeLimitedSubscriptionDialog() {
        this.showTimeLimitedSubscriptionDialog = true;
    }

    private navigateTo(url: string) {
        window.open(url);
    }
}
